<template>
  <div
    :class="{
      'product-list-view-fi': true,
      [`product-list-view-fi--${shopType}`]: true,
      [`product-list-view-fi--max-${maxHeadings}`]: true,
      [`product-list-view-fi--with-variant-image`]: showVariantImage,
      ...headerHeightClasses,
    }"
  >
    <div
      :class="{
        'product-list-view-fi__filter--fixed':
          filtersOutOfView && !isFite && facetsTransformed?.length > 0,
        'fi-hidden': hideFilters,
      }"
    >
      <div class="product-list-view-fi__filter-wrapper">
        <div
          class="product-list-view-fi__filter-container"
          :class="{
            'fi-shadow-lg': filtersOutOfView && !isFite,
          }"
        >
          <div
            v-if="filtersOutOfView && !isFite && isSM"
            class="product-list-view-fi__filter--expander"
            :class="{ 'fi-shadow-lg': !showFilters }"
            @click="toggleFilter"
          >
            <icon-fi
              icon="filter"
              class="product-list-view-fi__filter--expander-icon"
            />
            <div class="product-list-view-fi__filter--expander-text">
              {{ $dict.get('search.Fact-Finder.Search-Result.Filter') }}
            </div>
            <icon-fi
              class="product-list-view-fi__filter--expander-arrow"
              :icon="showFilters ? 'chevron-up' : 'chevron-down'"
            />
          </div>
          <slide-up-down-fi
            :model-value="showFilters || !filtersOutOfView"
            :duration="400"
          >
            <facets-menu-fi
              v-if="facetsTransformed?.length"
              :facets="facetsTransformed"
              :allow-clear-all="!autoFilters"
              :filters-out-of-view="filtersOutOfView"
              class="product-list-view-fi__facets"
              @change="changeFacet"
              @clear-all-facets="clearAllFacets"
            >
              <template
                #facets="{ facet, open, change, close, toggleOpen, idx, registerDomElement }"
              >
                <fact-finder-facet-fi
                  :facet="facet"
                  :open="open"
                  :idx="idx"
                  :register-dom-element="registerDomElement"
                  @change="change"
                  @toggle-open="toggleOpen(facet)"
                  @close="close(facet)"
                />
              </template>
              <template #selection="{ facet, element }">
                {{ cleanName(facet.name) }}:
                <span
                  v-if="facet.type === 'FLOAT' || facet.type === 'INTEGER'"
                  class="facets-selection-fi__value"
                >
                  {{ getLocalizedValue(element.selectedMinValue) }}
                  [{{ facet.unit }}] -
                  {{ getLocalizedValue(element.selectedMaxValue) }}
                  [{{ facet.unit }}]
                </span>
                <span
                  v-else
                  class="facets-selection-fi__value"
                >
                  {{ element.text }}
                </span>
              </template>
            </facets-menu-fi>
          </slide-up-down-fi>
        </div>
      </div>
    </div>
    <div
      v-if="!isFite"
      v-intersect="onIntersect"
    />
    <div
      v-if="!isFite"
      class="md:fi-mb-96"
    />
    <template v-if="searchProductsForFamilyNg?.totalHits !== 0">
      <div
        class="product-list-view-fi__wrapper"
        :class="`shop-type-${shopType}`"
        data-test="product-list-view-wrapper"
        :data-test-2="shopType === 'retailConnect' ? 'product-list-view-retail-connect' : null"
      >
        <div v-if="isLoading || isProductIsLoading">
          <loading-indicator
            variant="spinnerLocal"
            class="spinner-local--rebrush"
            :dark="true"
          />
        </div>

        <template v-else>
          <div class="product-list-view-fi__attribute-toggle">
            <product-attributes-toggle-fi
              v-if="hasApprovalAttributes"
              is-block
            />
          </div>

          <product-list-head-fi
            :total-hits="searchProductsForFamilyNg.totalHits"
            :category-attributes="getAttributes(paddedAttributes[products[0]?.ID])"
            :show-variant-image="showVariantImage"
          />
        </template>

        <accordion-customizable-wrapper-fi theme="plist">
          <accordion-customizable-fi
            v-for="(item, i) in products"
            :id="`accordion_${item.ID}`"
            :key="item.ID"
            :item-index="i"
            :shop-type="shopType ? shopType : 'default'"
            :label="item.catalogProductData.ProductName"
            :scroll-to="!isLG"
            theme="plist"
            data-test="accordion-customizable-item"
          >
            <template #headline>
              <div class="product-list-view-fi__header">
                <grid-container-fi
                  :container="false"
                  class="product-list-view-fi__header-main"
                  :grid-class="shopType !== 'closedShop' ? 'md:fi-gap-y-zero md:fi-gap-y-zero' : ''"
                >
                  <product-identifier-fi
                    :item="item"
                    :best-price="bestPrices[item.ID]"
                    :click-source="clickSource"
                    :show-variant-image="showVariantImage"
                  />

                  <product-attributes-fi
                    class="fi-hidden sm:fi-block"
                    :attributes="getAttributes(paddedAttributes[item.ID])"
                  />

                  <product-list-closed-shop-fi
                    v-if="isMounted && shopType === 'closedShop'"
                    :best-price="bestPrices[item.ID]"
                    :item="item"
                    :loading-prices="loadingPrices"
                    :is-adding-to-cart="isAddingItemToCart[item.ID]"
                    @remove-quantity-hints="removeQuantityHints"
                    @logout-if-token-invalid="logoutIfTokenInvalid"
                    @add-product="(productDetails) => addProduct(productDetails)"
                  />

                  <!-- RetailConnectCart -->
                  <template v-else-if="shopType === 'retailConnectCart'">
                    <product-buy-button-fi
                      :item="item"
                      :product-details="{
                        productID: item.ID,
                        productName: `${item.ID} - ${item.catalogProductData.ProductName}`,
                        productInitialQuantity: 1,
                      }"
                      :is-in-progress="
                        isAddingItemToCart[item.ID] ||
                        $apolloData.queries.getRetailerAvailabilityForIds.loading
                      "
                      :is-button-disabled="
                        productInCart(item) ||
                        isAddingItemToCart[item.ID] ||
                        retailerOutOfStock[i] ||
                        false
                      "
                      :is-button-hidden="hasAvailabilityStatusOnlyPermission"
                      @add-product="(productDetails) => addProduct(productDetails)"
                    />
                  </template>
                </grid-container-fi>

                <grid-container-fi
                  v-if="item.quantityIncreasedMessage"
                  :container="false"
                  class="product-list-view-fi__header-hint"
                >
                  <quantity-modification-hint-fi
                    class="product-list-view-fi__quantity-modification-hint"
                    :hint="item.quantityIncreasedMessage"
                    icon="info"
                    theme="product-list-view"
                  />
                </grid-container-fi>
              </div>
            </template>

            <template #content>
              <product-content-fi
                :item="item"
                :attributes="paddedAttributes[item.ID]"
                :family-line-drawing="familyLineDrawing"
                :best-price="bestPrices[item.ID]"
              />
            </template>
          </accordion-customizable-fi>
        </accordion-customizable-wrapper-fi>

        <div class="fi-hidden md:fi-block">
          <product-list-legend-fi v-if="showLegend" />
        </div>
      </div>

      <pagination-progress-bar
        v-if="searchProductsForFamilyNg?.totalHits"
        :pagination-page="pagination.page"
        :pagination-total="searchProductsForFamilyNg.totalHits || 0"
        :progressbar-value="pagingPercentage"
        :show-more-button="displayedItems < searchProductsForFamilyNg.totalHits"
        :query-params="filterUrlParams"
        :disabled-state="
          isProductIsLoading ||
          pagination.page * pagination.pageSize >= searchProductsForFamilyNg.totalHits
        "
        @loadmore="fetchMoreProducts"
      >
        <template
          v-if="pagination.page * pagination.pageSize <= searchProductsForFamilyNg.totalHits"
        >
          <span>{{
            $dict.get('product-List.Pagination.Show-Variants', [
              pagination.page * pagination.pageSize,
              searchProductsForFamilyNg.totalHits,
            ])
          }}</span>
        </template>

        <template
          v-else-if="pagination.page * pagination.pageSize > searchProductsForFamilyNg.totalHits"
        >
          <span>{{
            $dict.get('product-List.Pagination.Show-Variants', [
              searchProductsForFamilyNg.totalHits,
              searchProductsForFamilyNg.totalHits,
            ])
          }}</span>
        </template>
      </pagination-progress-bar>
    </template>
    <no-products-found-fi v-else />
    <div
      v-if="!isFite && isSM"
      v-intersect="onIntersectFilterOut"
      class="fi-relative fi-bottom-[300px]"
    />
  </div>
</template>

<script>
import {
  AccordionCustomizableFi,
  AccordionCustomizableWrapperFi,
  IconFi,
  LoadingIndicator,
  PaginationProgressBar,
  QuantityModificationHintFi,
  SlideUpDownFi,
} from 'atoms';
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';

import FacetsMenuFi from '@/components/facets/facets-menu-fi/FacetsMenuFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import autoFiltersMixin from '@/components/search-fact-finder-fi/autoFiltersMixin.vue';
import FactFinderFacetFi from '@/components/search-fact-finder-fi/fact-finder-facet-fi/FactFinderFacetFi.vue';
import factFinderNgNameCleaner from '@/components/search-fact-finder-fi/factFinderNgNameCleaner';
import mergeFacets from '@/components/search-fact-finder-fi/mergeFacets';
import stringifyFilterValue from '@/components/search-fact-finder-fi/stringifyFilterValue';
import toFilterInput from '@/components/search-fact-finder-fi/toFilterInput';
import { useAccessPrivileges } from '@/composables/accessPrivileges';
import { useHeaderHeight } from '@/composables/headerHeight';
import GET_FAMILY_BASE_FACETS_NG from '@/graphql/getFamilyBaseFacetsNg.gql';
import GET_RETAILER_AVAILABILITY_FOR_IDS from '@/graphql/getRetailerAvailabilityForIds.gql';
import SEARCH_PRODUCTS_FOR_FAMILY_NG from '@/graphql/searchProductsForFamilyNg.gql';
import { categoryFilterExpander } from '@/lib/fischerPlugin';
import breakpointMixin from '@/mixins/breakpointMixin.vue';
import localizedValueMixin from '@/mixins/localizedValueMixin.vue';
import multiBestPriceMixin from '@/mixins/multiBestPriceMixin.vue';
import sellingUnitMixin from '@/mixins/sellingUnitMixin.vue';
import shoppingcartMixin from '@/mixins/shoppingcartMixin.vue';
import userMixin from '@/mixins/userMixin.vue';

import ProductAttributesFi from './product-attributes-fi/ProductAttributesFi.vue';
import ProductAttributesToggleFi from './product-attributes-toggle-fi/ProductAttributesToggleFi.vue';
import ProductBuyButtonFi from './product-buy-button-fi/ProductBuyButtonFi.vue';
import ProductContentFi from './product-content-fi/ProductContentFi.vue';
import ProductIdentifierFi from './product-identifier-fi/ProductIdentifierFi.vue';
import ProductListHeadFi from './product-list-head-fi/ProductListHeadFi.vue';
import ProductListLegendFi from './product-list-legend-fi/ProductListLegendFi.vue';
const NoProductsFoundFi = defineAsyncComponent(() => import('./NoProductsFoundFi.vue'));

const ProductListClosedShopFi = defineAsyncComponent(
  () => import('./product-list-closed-shop-fi/ProductListClosedShopFi.vue'),
);

export default {
  name: 'ProductListViewFi',
  components: {
    PaginationProgressBar,
    LoadingIndicator,
    GridContainerFi,
    AccordionCustomizableFi,
    AccordionCustomizableWrapperFi,
    FacetsMenuFi,
    FactFinderFacetFi,
    NoProductsFoundFi,
    ProductAttributesFi,
    ProductAttributesToggleFi,
    ProductBuyButtonFi,
    ProductContentFi,
    ProductIdentifierFi,
    ProductListClosedShopFi,
    ProductListHeadFi,
    ProductListLegendFi,
    QuantityModificationHintFi,
    SlideUpDownFi,
    IconFi,
  },
  mixins: [
    shoppingcartMixin,
    sellingUnitMixin,
    multiBestPriceMixin,
    userMixin,
    autoFiltersMixin,
    localizedValueMixin,
    breakpointMixin,
  ],
  inject: ['fields'],
  props: {
    shopType: {
      type: String,
      default: '',
    },
    devData: {
      type: Object,
      default: null,
    },
    familyLineDrawing: {
      type: [String, null],
      default: null,
    },
    categoryAttributes: {
      type: Array,
      required: true,
    },
    approvals: {
      type: Array,
      default: () => [],
    },
    clickSource: {
      type: String,
      default: null,
    },
  },
  emits: ['ready'],
  setup() {
    const { headerHeightClasses } = useHeaderHeight();
    const { hasAvailabilityStatusOnlyPermission } = useAccessPrivileges();
    return { headerHeightClasses, hasAvailabilityStatusOnlyPermission };
  },
  data() {
    return {
      pagination: {
        page: 1, // current page of the "load more" button
        pageSize: 18,
        total: 0,
        initial: 1, // page set via url-param
      },
      isLoading: true,
      count: 0,
      showLegend: false,
      searchProductsForFamilyNg: null,
      getRetailerAvailabilityForIds: [],
      getFamilyBaseFacetsNg: null,
      filters: [],
      createdHook: true,
      isMounted: false,
      showFilters: true,
      filtersOutOfView: false,
      hideFilters: false,
    };
  },
  apollo: {
    getRetailerAvailabilityForIds: {
      query: GET_RETAILER_AVAILABILITY_FOR_IDS,
      variables() {
        const { baseSiteId } = this;

        return {
          baseSiteId,
          productIds: this.productIds,
          useGtins: this.$fischer.useGtins(),
        };
      },
      skip() {
        return !this.isMounted || !this.isRetailConnectCart || this.productIds.length === 0;
      },
    },
    searchProductsForFamilyNg: {
      query: SEARCH_PRODUCTS_FOR_FAMILY_NG,
      variables() {
        return this.createApolloVariables();
      },
      skip() {
        this.onCreated();
        if (IS_SHOWROOM) {
          return false;
        }
        return !this.baseSiteId || !this.$fischer.catalogId();
      },
      result({ data }) {
        if (data?.searchProductsForFamilyNg) {
          this.unselectedFacets = data.searchProductsForFamilyNg.facetDataUnselected;
          // shallow clone so we can modify the bestPrice
          this.products = data.searchProductsForFamilyNg.hits.map((hit) => ({
            ...(hit.variantValuesObject ? hit.variantValuesObject[0] : hit),
          }));
          this.pagination.total = data.searchProductsForFamilyNg.totalHits;
          this.setLegend();
        }
        this.isLoading = false;
        this.loadingQuantityInputs = {};
        this.isAddingItemToCart = {};

        this.$emit('ready');
      },
      error(error) {
        console.error(error);
      },
    },
    getFamilyBaseFacetsNg: {
      query: GET_FAMILY_BASE_FACETS_NG,
      variables() {
        const variables = this.createApolloVariables();
        return {
          factFinderChannel: variables.factFinderChannel,
          language: variables.language,
          id: variables.id,
          baseSiteId: variables.baseSiteId,
        };
      },
      skip() {
        return !this.baseSiteId || !this.$fischer.catalogId();
      },
    },
  },
  computed: {
    ...mapGetters({
      maxHeadings: 'productList/getMaxHeadings',
    }),
    products: {
      get() {
        return this.$store.getters['productList/getProducts'];
      },
      set(products) {
        this.$store.dispatch('productList/setProducts', products);
      },
    },
    loadingQuantityInputs: {
      get() {
        return this.$store.getters['productList/getLoadingQuantityInputs'];
      },
      set(inputDetails) {
        this.$store.dispatch('productList/setLoadingQuantityInputs', inputDetails);
      },
    },
    approvalsEnabled: {
      get() {
        return this.$store.getters['productList/getApprovalsEnabled'];
      },
      set(state) {
        this.$store.dispatch('productList/setApprovalsEnabled', state);
      },
    },
    baseSiteId() {
      if (IS_SHOWROOM && this.devData) {
        return this.devData.baseSiteId;
      }
      return this.$fischer.baseSiteId();
    },
    showVariantImage() {
      return Boolean(this.fields.showVariantImages?.value);
    },
    facetsLoaded() {
      return this.createdHook === false && this.getFamilyBaseFacetsNg;
    },
    // make sure every attribute of the category is present for each article
    // if it is missing, pad with empty string
    paddedAttributes() {
      const attributesPerRecord = {};
      this.searchProductsForFamilyNg.hits.forEach((record) => {
        if (!record || !record.variantValuesObject[0]) {
          console.error('ignoring record', record, JSON.stringify(this.searchProductsForFamilyNg));
          return;
        }
        const catalogAttributes = record.variantValuesObject[0].catalogProductData.Attributes || [];
        const attributes = [];
        let index = 0;
        const seen = new Set();
        this.categoryAttributes.forEach((categoryAttribute) => {
          seen.add(categoryAttribute.AttributeKey);
          if (catalogAttributes[index]?.AttributeKey === categoryAttribute.AttributeKey) {
            attributes.push(catalogAttributes[index]);
            index += 1;
          } else {
            const searchedIndex = catalogAttributes.indexOf(
              (attribute) => attribute.AttributeKey === categoryAttribute.AttributeKey,
            );
            if (searchedIndex >= 0) {
              attributes.push(catalogAttributes[searchedIndex]);
              index = searchedIndex + 1;
            } else {
              attributes.push({
                AttributeKey: categoryAttribute.AttributeKey,
                AttributeName: categoryAttribute.AttributeName,
                Value: '',
              });
            }
          }
        });
        catalogAttributes.forEach((catalogAttribute) => {
          if (!seen.has(catalogAttribute.AttributeKey)) {
            attributes.push(catalogAttribute);
          }
        });
        attributesPerRecord[record.variantValuesObject[0].ID] = attributes;
      });
      return attributesPerRecord;
    },
    displayedItems() {
      if (!this.searchProductsForFamilyNg) {
        return 0;
      }
      return Math.min(
        this.pagination.page * this.pagination.pageSize,
        this.searchProductsForFamilyNg.totalHits,
      );
    },
    pagingPercentage() {
      if (!this.searchProductsForFamilyNg) {
        return 0;
      }
      return (100 * this.displayedItems) / this.searchProductsForFamilyNg.totalHits;
    },
    isProductIsLoading() {
      if (!this.isMounted) {
        return false;
      }
      if (
        !this.$apollo.queries.searchProductsForFamilyNg ||
        this.$apollo.queries.searchProductsForFamilyNg.loading
      ) {
        return true;
      }
      return false;
    },
    isRetailConnect() {
      return this.$fischer.shop.isRetailConnect();
    },
    isRetailConnectCart() {
      return this.$fischer.shop.isRetailConnectCart();
    },
    retailerOutOfStock() {
      return (this.getRetailerAvailabilityForIds || []).map((availability) => {
        if (!availability) {
          // something went wrong, default to 'can search for retailers'
          return false;
        }
        return availability.stock === 'empty' || availability.stock === null;
      });
    },
    productIds() {
      const products = this.searchProductsForFamilyNg?.hits || [];
      return products.map((product) =>
        product.variantValuesObject ? product.variantValuesObject[0].ID : product.ID,
      );
    },
    filterUrlParams() {
      const search = [];

      this.filters.forEach((filter) => {
        search.push(
          `filter${encodeURIComponent(filter.associatedFieldName)}=${encodeURIComponent(
            stringifyFilterValue(filter),
          )}`,
        );
      });

      if (search.length === 0) {
        return '';
      }

      return `?${search.sort().join('&')}`;
    },
    facetsTransformed() {
      if (!this.searchProductsForFamilyNg) {
        return [];
      }
      return mergeFacets(
        this.searchProductsForFamilyNg.facetDataUnselected,
        this.searchProductsForFamilyNg.facets,
        this.getFamilyBaseFacetsNg || [],
        this.autoFilters || this.filters,
      );
    },
    paginationPageIndex() {
      return this.pagination.pageSize * this.pagination.page;
    },
    hasApprovalAttributes() {
      if (!this.products?.length) {
        return false;
      }
      const attributes = this.paddedAttributes[this.products[0]?.ID];

      if (!attributes || !this.approvals) {
        return false;
      }

      return attributes.some((a) => this.approvals.includes(a.AttributeKey));
    },
  },
  watch: {
    facetsLoaded(value) {
      if (!value) {
        return;
      }
      // clear filters set via url-param but not in base-facets
      this.filters.forEach((activeFilter) => {
        const baseFilter = this.getFamilyBaseFacetsNg.find(
          (baseFacet) => baseFacet.associatedFieldName === activeFilter.associatedFieldName,
        );
        const firstElement = baseFilter?.elements?.[0];
        if (
          !firstElement ||
          (firstElement.absoluteMinValue !== null &&
            firstElement.absoluteMinValue !== undefined &&
            firstElement.absoluteMinValue === firstElement.absoluteMaxValue)
        ) {
          this.changeFacet({
            value: false,
            element: {
              selected: false,
              name: activeFilter.name,
              associatedFieldName: activeFilter.associatedFieldName,
            },
          });
        }
      });
    },
  },
  created() {
    this.onCreated();
  },
  mounted() {
    const storedApprovalsEnabled = localStorage.getItem('productList:approvalsEnabled');
    if (storedApprovalsEnabled === 'true') {
      this.approvalsEnabled = true;
    }

    if (this.$fischer.cartId() && (this.$fischer.shop.isClosedShop() || this.isRetailConnectCart)) {
      this.getCart(false, true);
    }
    try {
      const filterExpand = JSON.parse(window.sessionStorage.getItem(categoryFilterExpander));
      if (filterExpand === false) {
        this.showFilters = filterExpand;
      }
    } catch (error) {
      console.error(error);
    }
    this.isMounted = true;
  },
  methods: {
    onCreated() {
      if (this.createdHook === 'onCreated' || this.createdHook === false) {
        return;
      }
      this.createdHook = 'onCreated';

      const initialPage = Number(this.$fischer.query('page')) || 1;
      this.pagination.initial = initialPage;
      this.pagination.page = initialPage;

      this.parseQueryParams();

      this.createdHook = false;
    },
    cleanName(name) {
      return factFinderNgNameCleaner(name);
    },
    factFinderNgNameCleaner(name) {
      return factFinderNgNameCleaner(name);
    },
    async addProduct(data) {
      const product = this.products.find((p) => p.ID === data.productID);
      if (product) {
        delete product.quantityIncreasedMessage;
      }

      this.addProductToCart(data);
    },
    parseQueryParams() {
      const changes = [];
      const search = this.$fischer.query();
      Object.keys(search).forEach((key) => {
        const match = /filter(.*)/.exec(key);
        if (!match) {
          return;
        }

        const [, name] = match;

        if (/[^~]~~[^~]/.test(name)) {
          const [nameShort] = name.split('~~');
          const values = /^([0-9.]+)-([0-9.]+)/.exec(search[key]);
          if (values) {
            changes.push({
              element: {
                name: nameShort,
                associatedFieldName: name,
                selectedMinValue: values[1],
                selectedMaxValue: values[2],
                selected: true,
              },
              value: true,
            });
            return;
          }
        }
        changes.push({
          element: {
            associatedFieldName: name,
            name: search[key],
            selected: true,
          },
          value: true,
        });
      });
      this.changeFacet(...changes);
    },
    changeFacet(...changes) {
      this.applyChanges(...changes);
      if (!this.useAutoFilters) {
        this.updateQueries();
        this.pushUriState();
      }
    },
    pushUriState() {
      if (IS_SSR) {
        return;
      }

      const customQuery = [];
      const query = this.$fischer.query();
      const filterKeys = ['filter', 'query'];
      Object.entries(query).forEach(([key, value]) => {
        if (!filterKeys.some((el) => key.startsWith(el))) {
          customQuery.push(`${key}=${encodeURIComponent(value)}`);
        }
      });

      if (!this.filterUrlParams && !customQuery.length && !window.location.search) {
        return;
      }

      let state = this.filterUrlParams;
      if (customQuery.length) {
        if (this.filterUrlParams) {
          state = `${this.filterUrlParams}&${customQuery.join('&')}`;
        } else {
          state = `?${customQuery.join('&')}`;
        }
      }

      window.history.replaceState('', '', state || '?');
    },
    clearAllFacets() {
      this.enableAutoFilters();
      this.filters = [];
      if (!this.createdHook) {
        this.updateQueries();
        this.pushUriState();
      }
    },
    productInCart(item) {
      let inCart = false;
      if (this.cart?.products) {
        inCart = this.cart.products.some(
          (product) => product.articleNo === item.catalogProductData.ProductId,
        );
      }
      return inCart;
    },
    createApolloVariables() {
      let id = this.$fischer.catalogId('1001340182');
      let language = this.$fischer.catalogLocale();
      let catalogLocale = this.$fischer.catalogLocale();

      // Use development data in development environment
      if (IS_SHOWROOM && this.devData) {
        id = this.devData.id;
        language = this.devData.language;
        catalogLocale = this.devData.language.replace('_', '-');
      }

      const extraFilters = [
        {
          associatedFieldName: 'Hidden_K',
          name: '!true',
        },
      ];

      return {
        id,
        factFinderChannel: this.$fischer.factFinderChannel(),
        channel: this.$fischer.brandId(),
        brand: this.$fischer.brandId(),
        baseSiteId: this.$fischer.baseSiteId(),
        language,
        hostname: this.$fischer.hostname(),
        page: this.pagination.initial,
        quantity: this.pagination.pageSize,
        accessToken: this.$fischer.user.accessToken(),
        userId: this.$fischer.user.id(),
        filters: (this.autoFilters || this.filters).map(toFilterInput),
        extraFilters,
        siteSecure: this.$fischer.isSecureConnection(),
        catalogLocale,
        maxHeadings: this.maxHeadings,
        noSitecoreLinks: true,
        useGtins: this.$fischer.useGtins(),
        division: this.$fischer.user.division(),
        distributionChannel: this.$fischer.user.distributionChannel(),
      };
    },
    updateQueries() {
      if (this.createdHook) {
        return;
      }
      this.pagination.initial = 1;
      this.pagination.page = 1;
      this.count += 1;
    },
    fetchMoreProducts() {
      this.pagination.page = this.pagination.page + 1;

      this.$apollo.queries.searchProductsForFamilyNg.fetchMore({
        variables: {
          ...this.createApolloVariables(),
          page: this.pagination.page,
          includeUnselectedFacets: false,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const ret = {
            searchProductsForFamilyNg: {
              ...previousResult.searchProductsForFamilyNg,
              hits: [
                ...previousResult.searchProductsForFamilyNg.hits,
                ...fetchMoreResult.searchProductsForFamilyNg.hits,
              ],
            },
          };
          return ret;
        },
      });
    },
    getBestPriceForItem(item) {
      return this.bestPrices[item?.ID] || null;
    },
    setLegend() {
      this.showLegend = false;
      this.products.forEach(({ catalogProductData: { Attributes } = {} }) => {
        (Attributes || []).forEach(({ AttributeKey }) => {
          if (this.approvals.includes(AttributeKey)) {
            this.showLegend = true;
          }
        });
      });
    },
    removeQuantityHints() {
      this.products.forEach((p) => {
        delete p.quantityIncreasedMessage;
      });
    },
    getAttributes(attributes) {
      if (!attributes) {
        return [];
      }
      const attributesWithoutApprovals = attributes.filter(
        (a) => !this.approvals.includes(a.AttributeKey),
      );
      const allAttributes = this.approvalsEnabled ? attributes : attributesWithoutApprovals;
      const reducedAttributes = allAttributes.slice(0, this.maxHeadings);
      return reducedAttributes;
    },
    onIntersect(observer) {
      // if (
      //   !observer?.[0]?.isIntersecting &&
      //   observer[0]?.target?.getBoundingClientRect?.()?.top <= 0
      // ) {
      //   this.filtersOutOfView = true;
      //   this.$store.commit('product/SET_IS_FILTERS_FIXED', true);
      // } else {
      //   this.filtersOutOfView = false;
      //   this.$store.commit('product/SET_IS_FILTERS_FIXED', false);
      // }
    },
    onIntersectFilterOut(observer) {
      if (
        !observer?.[0]?.isIntersecting &&
        observer[0]?.target?.getBoundingClientRect?.()?.top <= 0
      ) {
        this.hideFilters = true;
      } else {
        this.hideFilters = false;
      }
    },
    toggleFilter() {
      this.showFilters = !this.showFilters;
      try {
        window.sessionStorage.setItem(categoryFilterExpander, this.showFilters);
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<style scoped src="./product-list-view-fi" />
